import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import {
  BotGuild,
  ChannelConfig,
  ChannelSetupInfo,
  CustomEmoji,
  CustomSignupTemplate,
  GearInfo,
  GuildOverview,
  RepeatingEvent,
  ServerInfo,
  SignupGroupTemplate,
  UserConfig,
} from '../models/bot/guild';
import { StatisticsData } from '../models/bot/statistics';
import { Me, SessionInfo } from '../models/bot/me';
import {
  CatalogueAuthorResubmitRequest,
  CatalogueGame,
  CatalogueGameAdmin,
  CatalogueGameDeleteResponse,
  CatalogueListResponse,
  CatalogueReviewEditRequest,
  CatalogueShareRequest,
  CatalogueTemplate,
  CatalogueTemplateDeleteResponse,
  CatalogueUpdateRequest,
} from '../models/bot/catalogue';
import { catchError, take, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { HttpParams } from '@angular/common/http';
import { httpErrorMessage } from './http-error-message';

@Injectable({
  providedIn: 'root',
})
export class BotResourceService {
  constructor(private http: HttpClient, private toastr: ToastrService) {}

  getPersonalData(): Observable<Me> {
    return this.http.get<Me>('api/me');
  }

  getSession(): Observable<SessionInfo> {
    return this.http.get<SessionInfo>('api/internal/session');
  }

  getGuildsList(allBotGuilds = false): Observable<BotGuild[]> {
    const params = allBotGuilds ? { all_bot_guilds: 'true' } : {};
    return this.http.get<BotGuild[]>('api/internal/guilds', { params });
  }

  getGuild(guildId: string): Observable<BotGuild> {
    return this.http
      .get<BotGuild>(`api/internal/guilds/${guildId}`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          // if the error is 404, the guild is not found
          // than redirect to the invite page
          if (error.status === 404) {
            this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
            return throwError(error);
          }
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  postCommand(
    guildId: string,
    command: string,
    value: string,
    channelId?: string
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>('api/internal/guild', {
      guild_id: guildId,
      command: command,
      value: value,
      channel_id: channelId,
    });
  }

  getStatisticsData(): Observable<StatisticsData> {
    return this.http.get<StatisticsData>('api/internal/statistics');
  }

  exchangeGear(
    guildId: string,
    exchangeService: string,
    reminder: number
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>('api/internal/guild', {
      guild_id: guildId,
      command: 'gears guild consume',
      value: exchangeService,
      reminder: reminder,
    });
  }

  getGuildOverview(guildId: string): Observable<any> {
    return this.http
      .get<GuildOverview>(`api/internal/guilds/${guildId}/overview`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getGoogleCalendarStatus(): Observable<{ connected: boolean; configured: boolean }> {
    return this.http.get<{ connected: boolean; configured: boolean }>(
      'api/internal/google-calendar/status'
    );
  }

  getGoogleCalendarAuthUrl(redirectPath?: string): Observable<{ auth_url: string }> {
    const params: any = {};
    if (redirectPath) params['redirect_path'] = redirectPath;
    return this.http.get<{ auth_url: string }>('api/internal/google-calendar/auth-url', { params });
  }

  getGoogleCalendarEvents(timeMin: string, timeMax: string): Observable<{ events: Array<{ title: string; start: string; end?: string }> }> {
    return this.http.get<{ events: Array<{ title: string; start: string; end?: string }> }>(
      'api/internal/google-calendar/events',
      { params: { time_min: timeMin, time_max: timeMax } }
    );
  }

  disconnectGoogleCalendar(): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>('api/internal/google-calendar/disconnect', {});
  }

  getCalendarConflicts(guildId: string): Observable<{ conflicts: Record<string, Array<{ title: string; start: string; end?: string }>> }> {
    return this.http.get<{ conflicts: Record<string, Array<{ title: string; start: string; end?: string }>> }>(
      `api/internal/guilds/${guildId}/calendar-conflicts`
    );
  }

  getGuildServer(guildId: string): Observable<any> {
    return this.http
      .get<ServerInfo>(`api/internal/guilds/${guildId}/server`, {
        params: { guild_id: guildId },
      })
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getGuildChannel(guildId: string, channelId: string): Observable<any> {
    return this.http
      .get<ChannelConfig>(`api/internal/guilds/${guildId}/channels/${channelId}`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getGuildChannelSetupInfo(guildId: string): Observable<ChannelSetupInfo> {
    return this.http
      .get<ChannelSetupInfo>(`api/internal/guilds/${guildId}/channel-setup-info`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error);
        })
      );
  }

  getSignupTemplate(
    guildId: string,
    signupTemplateId: string
  ): Observable<any> {
    return this.http
      .get<CustomSignupTemplate>(
        `api/internal/guilds/${guildId}/signupTemplate/${signupTemplateId}`
      )
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getSignupTemplates(guildId: string): Observable<any> {
    return this.http
      .get<any>(`api/internal/guilds/${guildId}/signupTemplate`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getGuildMembers(guildId: string): Observable<any> {
    return this.http.get<any>(`api/internal/guilds/${guildId}/member`).pipe(
      catchError((error) => {
        const errorMessage =
            httpErrorMessage(error);
        this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
        return throwError(error); // Re-throw the error for further handling if needed
      })
    );
  }

  getEventCreateInfos(guildId: string): Observable<any> {
    return this.http
      .get<any>(`api/internal/guilds/${guildId}/eventCreateInfos`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  postSignupTemplate(
    guildId: string,
    signupTemplateId: string,
    template: string,
    idChange: boolean
  ): any {
    const url = `api/internal/guilds/${guildId}/signupTemplate/${signupTemplateId}`;

    // Send the PATCH request
    return this.http
      .patch<CustomSignupTemplate>(url, {
        template: template,
        id_change: idChange,
      })
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Template updated successfully!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          // Extract error message or default to 'Unknown error.'
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  createSignupTemplate(guildId: string, template: string): Observable<any> {
    return this.http
      .post<CustomSignupTemplate>(
        `api/internal/guilds/${guildId}/signupTemplate`,
        { template: template, id_change: true },
        { observe: 'response' }
      )
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Template successfully created!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  deleteSignupTemplate(
    guildId: string,
    signupTemplateId: string
  ): Observable<any> {
    return this.http
      .delete<CustomSignupTemplate>(
        `api/internal/guilds/${guildId}/signupTemplate/${signupTemplateId}`,
        { observe: 'response' }
      )
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Template successfully deleted!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  createEvent(guildId: string, event: any): Observable<any> {
    return this.http
      .post<CustomEvent>(`api/internal/guilds/${guildId}/event`, event, {
        observe: 'response',
      })
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Event successfully created!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }


  createRepeatingEvent(guildId: string, event: any): Observable<any> {
    return this.http
      .post<CustomEvent>(`api/internal/guilds/${guildId}/repeatingEvent`, event, {
        observe: 'response',
      })
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Repeating Event successfully created!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  updateRepeatingEvent(guildId: string, event: any, id: string): Observable<any> {
    return this.http
      .patch<CustomEvent>(`api/internal/guilds/${guildId}/repeatingEvent/${id}`, event, {
        observe: 'response',
      })
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Repeating Event successfully updated!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getRepeatingEvent(
    guildId: string,
    repeatingEventId: string
  ): Observable<any> {
    return this.http
      .get<RepeatingEvent>(
        `api/internal/guilds/${guildId}/repeatingEvent/${repeatingEventId}`
      )
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }


  deleteRepeatingEvent(
    guildId: string,
    channelId: string,
    repeatingEventId: string
  ): Observable<any> {
    return this.http
      .delete<RepeatingEvent>(
        `api/internal/guilds/${guildId}/repeatingEvent/${repeatingEventId}`,
        { params: {channel_id: channelId}},
      )
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Repeating Event successfully deleted!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getGuildEmoji(guildId: string): Observable<any> {
    return this.http
      .get<CustomEmoji>(`api/internal/guilds/${guildId}/emojis`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  gearExchange(
    guilId: string,
    view_value: string,
    value: string,
    reminder: number,
    actionCallback?: () => void
  ): void {
    // console.log('gearExchange', guilId, value, reminder)
    this.exchangeGear(guilId, value, reminder)
      .pipe(take(1))
      .subscribe(
        () => {
          this.toastr.success('Saved successfully', view_value, {
            timeOut: 2000,
          });
          if (actionCallback) {
            actionCallback();
          }
        },
        (error) => {
          const errorMessage =
            httpErrorMessage(error, 'Unknown error. Could not save.');
          this.toastr.error(errorMessage, view_value, { timeOut: 2000 });
        }
      );
  }

  sendCommand(
    guilId: string,
    command: string,
    value: string,
    channelId?: string,
    actionCallback?: () => void,
    errorCallback?: () => void
  ): void {
    // console.log('sendCommand', guilId, command, value, channelId)
    this.postCommand(guilId, command, value, channelId)
      .pipe(take(1))
      .subscribe(
        () => {
          this.toastr.success('Saved successfully', command, { timeOut: 2000 });
          if (actionCallback) {
            actionCallback();
          }
        },
        (error) => {
          const errorMessage =
            httpErrorMessage(error, 'Unknown error. Could not save.');
          this.toastr.error(errorMessage, command, { timeOut: 2000 });
          if (errorCallback) {
            errorCallback();
          }
        }
      );
  }

  getGuildUser(guildId: string) {
    return this.http
      .get<UserConfig>('api/internal/guild/user', {
        params: { guild_id: guildId },
      })
      .pipe(
        catchError((error) => {
          const errorMessage = httpErrorMessage(error, 'Unknown error. Could not fetch user info.');
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getGuildGear(guildId: string): Observable<GearInfo> {
    return this.http
      .get<GearInfo>(`api/internal/guilds/${guildId}/gears`)
      .pipe(
        catchError((error) => {
          const errorMessage = httpErrorMessage(error, 'Unknown error. Could not fetch gear info.');
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  createSignupGroup(guildId: string, payload: any): Observable<any> {
    return this.http
      .post<SignupGroupTemplate>(
        `api/internal/guilds/${guildId}/signupGroup`,
        payload,
        { observe: 'response' }
      )
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Signup Group successfully created!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  updateSignupGroup(guildId: string, signupgroupId: string, payload: any): Observable<any> {
    return this.http
      .patch<SignupGroupTemplate>(
        `api/internal/guilds/${guildId}/signupGroup/${signupgroupId}`,
        payload
      )
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Signup Group successfully updated!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  deleteSignupGroup(guildId: string, signupgroupId: string): Observable<any> {
    return this.http
      .delete<SignupGroupTemplate>(
        `api/internal/guilds/${guildId}/signupGroup/${signupgroupId}`
      )
      .pipe(
        tap({
          next: () => {
            // Success handler
            this.toastr.success('Signup Group successfully deleted!', 'Success', {
              timeOut: 3000,
            });
          },
          error: (error) => {
            // Error handler within the tap is only for logging; actual error handling is in catchError
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getSignupGroup(guildId: string, signupgroupId: string): Observable<any> {
    return this.http
      .get<CustomSignupTemplate>(`api/internal/guilds/${guildId}/signupGroup/${signupgroupId}`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  getSignupGroups(guildId: string): Observable<any> {
    return this.http
      .get<SignupGroupTemplate[]>(`api/internal/guilds/${guildId}/signupGroup`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
          return throwError(error); // Re-throw the error for further handling if needed
        })
      );
  }

  listCatalogueTemplates(options: {
    q?: string;
    game_id?: number | null;
    sort?: string;
    limit?: number;
    offset?: number;
  } = {}): Observable<CatalogueListResponse> {
    let params = new HttpParams();
    if (options.q) {
      params = params.set('q', options.q);
    }
    if (options.game_id != null) {
      params = params.set('game_id', String(options.game_id));
    }
    if (options.sort) {
      params = params.set('sort', options.sort);
    }
    if (options.limit != null) {
      params = params.set('limit', String(options.limit));
    }
    if (options.offset != null) {
      params = params.set('offset', String(options.offset));
    }
    return this.http.get<CatalogueListResponse>('api/catalogue/templates', { params });
  }

  getCatalogueTemplate(id: number): Observable<CatalogueTemplate> {
    return this.http.get<CatalogueTemplate>(`api/catalogue/templates/${id}`);
  }

  listMyCatalogueTemplates(): Observable<CatalogueListResponse> {
    return this.http.get<CatalogueListResponse>('api/catalogue/mine').pipe(
      catchError((error) => {
        const errorMessage =
          httpErrorMessage(error);
        this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
        return throwError(error);
      })
    );
  }

  listCatalogueGames(q?: string): Observable<CatalogueGame[]> {
    let params = new HttpParams();
    if (q) {
      params = params.set('q', q);
    }
    return this.http.get<CatalogueGame[]>('api/catalogue/games', { params });
  }

  listCatalogueGamesAdmin(q?: string): Observable<CatalogueGameAdmin[]> {
    let params = new HttpParams();
    if (q) {
      params = params.set('q', q);
    }
    return this.http.get<CatalogueGameAdmin[]>('api/catalogue/admin/games', { params }).pipe(
      catchError((error) => {
        const errorMessage =
          httpErrorMessage(error);
        this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
        return throwError(error);
      })
    );
  }

  voteCatalogueTemplate(id: number, kind: 'like' | 'dislike'): Observable<CatalogueTemplate> {
    return this.http
      .post<CatalogueTemplate>(`api/catalogue/templates/${id}/vote`, { kind })
      .pipe(
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  shareSignupTemplate(
    guildId: string,
    templateId: string,
    body: CatalogueShareRequest
  ): Observable<CatalogueTemplate> {
    return this.http
      .post<CatalogueTemplate>(
        `api/internal/guilds/${guildId}/signupTemplate/${templateId}/share`,
        body
      )
      .pipe(
        tap({
          next: () => {
            this.toastr.success('Template submitted for review!', 'Success', {
              timeOut: 3000,
            });
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  importCatalogueTemplate(
    guildId: string,
    catalogueId: number
  ): Observable<{ success: boolean; id: string; name: string }> {
    return this.http
      .post<{ success: boolean; id: string; name: string }>(
        `api/internal/guilds/${guildId}/catalogue/${catalogueId}/import`,
        {}
      )
      .pipe(
        tap({
          next: () => {
            this.toastr.success('Template imported!', 'Success', { timeOut: 3000 });
          },
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  listCatalogueReview(): Observable<CatalogueListResponse> {
    return this.http.get<CatalogueListResponse>('api/catalogue/review').pipe(
      catchError((error) => {
        const errorMessage =
          httpErrorMessage(error);
        this.toastr.error(errorMessage, 'Error', { timeOut: 2000 });
        return throwError(error);
      })
    );
  }

  approveCatalogueTemplate(
    id: number,
    body: CatalogueReviewEditRequest
  ): Observable<CatalogueTemplate> {
    return this.http
      .post<CatalogueTemplate>(`api/catalogue/review/${id}/approve`, body)
      .pipe(
        tap({
          next: () => this.toastr.success('Template approved', 'Success', { timeOut: 2000 }),
        }),
        catchError((error) => {
          const errorMessage = httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  denyCatalogueTemplate(id: number): Observable<CatalogueTemplate> {
    return this.http
      .post<CatalogueTemplate>(`api/catalogue/review/${id}/deny`, {})
      .pipe(
        tap({
          next: () => this.toastr.success('Template denied', 'Success', { timeOut: 2000 }),
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  updateCatalogueTemplate(id: number, body: CatalogueUpdateRequest): Observable<CatalogueTemplate> {
    return this.http
      .patch<CatalogueTemplate>(`api/catalogue/templates/${id}`, body)
      .pipe(
        tap({
          next: () => this.toastr.success('Template updated', 'Success', { timeOut: 2000 }),
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  resubmitCatalogueTemplate(
    id: number,
    body: CatalogueAuthorResubmitRequest
  ): Observable<CatalogueTemplate> {
    return this.http
      .post<CatalogueTemplate>(`api/catalogue/templates/${id}/resubmit`, body)
      .pipe(
        tap({
          next: () =>
            this.toastr.success('Revision submitted for review', 'Success', { timeOut: 3000 }),
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  unlistCatalogueTemplate(id: number): Observable<CatalogueTemplate> {
    return this.http
      .post<CatalogueTemplate>(`api/catalogue/templates/${id}/unlist`, {})
      .pipe(
        tap({
          next: () => this.toastr.success('Template unlisted', 'Success', { timeOut: 2000 }),
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  relistCatalogueTemplate(id: number): Observable<CatalogueTemplate> {
    return this.http
      .post<CatalogueTemplate>(`api/catalogue/templates/${id}/relist`, {})
      .pipe(
        tap({
          next: () => this.toastr.success('Template listed again', 'Success', { timeOut: 2000 }),
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  deleteCatalogueTemplate(id: number): Observable<CatalogueTemplateDeleteResponse> {
    return this.http
      .delete<CatalogueTemplateDeleteResponse>(`api/catalogue/templates/${id}`)
      .pipe(
        tap({
          next: () => this.toastr.success('Template deleted', 'Success', { timeOut: 2000 }),
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }

  deleteCatalogueGame(gameId: number): Observable<CatalogueGameDeleteResponse> {
    return this.http
      .delete<CatalogueGameDeleteResponse>(`api/catalogue/games/${gameId}`)
      .pipe(
        tap({
          next: () => this.toastr.success('Game removed', 'Success', { timeOut: 2000 }),
        }),
        catchError((error) => {
          const errorMessage =
            httpErrorMessage(error);
          this.toastr.error(errorMessage, 'Error', { timeOut: 3000 });
          return throwError(error);
        })
      );
  }
}
